import { useCallback, useMemo, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Button as RadixButton } from "@radix-ui/themes";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings2 } from "lucide-react";
import { ColumnSearchInput } from "./explore-display-options/ColumnSearchInput";
import { useExploreCustomFields } from "./useExploreCustomFields";

type ViewColumn = "title" | "source" | "createdAt" | "updatedAt";

interface ViewsDisplayOptionsProps {
  visibleColumns: ViewColumn[];
  onChange: (columns: ViewColumn[]) => void;
}

const columnLabels: Record<ViewColumn, string> = {
  title: "Title",
  source: "Source",
  createdAt: "Created At",
  updatedAt: "Updated At",
};

const allColumns: ViewColumn[] = ["title", "source", "createdAt", "updatedAt"];
const mandatoryColumns: ViewColumn[] = ["title"];

export function ExploreViewsDisplayOptions({
  visibleColumns,
  onChange,
}: ViewsDisplayOptionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchColumns, setSearchColumns] = useState("");
  const customFields = useExploreCustomFields();

  const availableColumns = useMemo(() => {
    return [...allColumns].filter(
      (column) => !mandatoryColumns.includes(column)
    );
  }, [customFields]);

  const handleColumnToggle = useCallback(
    (column: ViewColumn) => {
      const newColumns = visibleColumns.includes(column)
        ? visibleColumns.filter((c) => c !== column)
        : [...visibleColumns, column];

      onChange(newColumns);
    },
    [visibleColumns, onChange]
  );

  const filteredColumns = useMemo(() => {
    if (!searchColumns) return availableColumns;
    return availableColumns.filter((column) =>
      columnLabels[column].toLowerCase().includes(searchColumns.toLowerCase())
    );
  }, [availableColumns, searchColumns]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger>
        <RadixButton variant="outline" size="2">
          <Settings2 className="size-4" />
          Display
        </RadixButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        style={{ width: "280px", maxHeight: "400px" }}
        align="end"
      >
        <div className="flex flex-col gap-2 px-2 py-1">
          <label className="text-sm text-gray-500">Display columns</label>

          <ScrollArea className="max-h-[200px]">
            <div className="flex flex-col gap-1">
              {mandatoryColumns.map((column) => (
                <Button
                  key={column}
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 px-2 py-1"
                  disabled
                >
                  <Checkbox checked disabled />
                  {columnLabels[column]}
                </Button>
              ))}
              {filteredColumns.map((column) => (
                <Button
                  key={column}
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 px-2 py-1"
                  onClick={() => handleColumnToggle(column)}
                >
                  <Checkbox
                    checked={visibleColumns.includes(column)}
                    onCheckedChange={() => handleColumnToggle(column)}
                  />
                  {columnLabels[column]}
                </Button>
              ))}
            </div>
          </ScrollArea>

          <div className="py-1">
            <ColumnSearchInput
              value={searchColumns}
              onChange={setSearchColumns}
              placeholder="Search columns"
            />
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
